import { useState, type FormEvent } from "react";
import { Plus, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ListTableShell } from "@/components/ResourceShell";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ReleaseChannelBadge } from "@/components/StatusBadges";
import { useCells, useReleases } from "@/api/queries/provisioning";
import { usePromoteRelease, useRegisterRelease, useRollout } from "@/api/mutations/provisioning";
import { relativeTime } from "@/lib/relativeTime";
import type { Release, RolloutResult } from "@/types";

const SELECT_CLASS = "flex h-10 w-full rounded-md border border-border bg-base px-3 py-2 text-sm text-text";

function RegisterDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const register = useRegisterRelease();
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    register.mutate({ version: version.trim(), notes }, { onSuccess: () => onOpenChange(false) });
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="register-release-dialog">
        <DialogHeader>
          <DialogTitle>Release registrieren</DialogTitle>
          <DialogDescription>Normalerweise meldet die Pipeline das Release. Es steht danach als beta; stable ist der bewusste Freigabe-Klick in der Liste.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="rel-version">Version (Image-Tag)</Label><Input id="rel-version" value={version} onChange={(e) => setVersion(e.target.value)} required placeholder="v2026.9.1" data-testid="release-version" /></div>
          <div className="space-y-2"><Label htmlFor="rel-notes">Notizen</Label><Textarea id="rel-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} /></div>
          {register.isError && <div className="text-sm text-danger">{register.error.message}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={register.isPending}>Abbrechen</Button>
            <Button type="submit" disabled={register.isPending} data-testid="release-submit">{register.isPending ? "…" : "Registrieren"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RolloutDialog({ open, onOpenChange, release }: { open: boolean; onOpenChange: (o: boolean) => void; release: Release }) {
  const cells = useCells();
  const rollout = useRollout();
  const [cellId, setCellId] = useState("");
  const [results, setResults] = useState<RolloutResult[] | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rollout-dialog">
        <DialogHeader>
          <DialogTitle>Rollout {release.version}</DialogTitle>
          <DialogDescription>Setzt die Version fuer alle Kunden einer Zelle (oder aller Zellen). stable-Kunden bekommen nur freigegebene Releases; jeder Kunde wartet selbst auf sein Wartungsfenster. Staffelung: eigener Mandant → Piloten → alle.</DialogDescription>
        </DialogHeader>
        {!results ? (
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="ro-cell">Zelle</Label>
              <select id="ro-cell" value={cellId} onChange={(e) => setCellId(e.target.value)} className={SELECT_CLASS} data-testid="rollout-cell"><option value="">alle Zellen</option>{(cells.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            </div>
            {rollout.isError && <div className="text-sm text-danger">{rollout.error.message}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={rollout.isPending}>Abbrechen</Button>
              <Button onClick={() => rollout.mutate({ version: release.version, ...(cellId ? { cellId } : {}) }, { onSuccess: (r) => setResults(r.results) })} disabled={rollout.isPending} data-testid="rollout-submit">{rollout.isPending ? "…" : "Ausrollen"}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            {results.length === 0 ? <div className="text-sm text-text3">Keine Kunden betroffen.</div> : (
              <ul className="space-y-1 text-sm" data-testid="rollout-results">
                {results.map((r) => <li key={r.specId} className="flex justify-between"><span>{r.customer}</span><span className="mono text-xs text-text3">{r.result}</span></li>)}
              </ul>
            )}
            <DialogFooter><Button onClick={() => onOpenChange(false)}>Schliessen</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// #343 UC-5/UC-6: Releases (Pipeline registriert beta, Freigabe = stable) + Rollout je Zelle.
export function ReleasesPage() {
  const releases = useReleases();
  const promote = usePromoteRelease();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState<Release | null>(null);
  const [rolloutTarget, setRolloutTarget] = useState<Release | null>(null);
  const all = releases.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text3">{all.length} Releases · {all.filter((r) => r.channel === "stable").length} freigegeben</div>
        <Button onClick={() => setRegisterOpen(true)} data-testid="register-release-button"><Plus className="h-4 w-4" /> Release registrieren</Button>
      </div>
      <ListTableShell isLoading={releases.isLoading} isError={releases.isError} errorMessage={releases.error?.message} isEmpty={all.length === 0} emptyMessage="Noch keine Releases" emptyHint="release.yml meldet Releases als beta; stable ist der Freigabe-Klick." onRetry={() => releases.refetch()}>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Version</TableHead><TableHead>Kanal</TableHead><TableHead>Registriert</TableHead><TableHead>Freigegeben</TableHead><TableHead>Notizen</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {all.map((r) => (
              <TableRow key={r.version}>
                <TableCell className="mono text-xs font-medium">{r.version}</TableCell>
                <TableCell><ReleaseChannelBadge channel={r.channel} /></TableCell>
                <TableCell className="text-text3">{relativeTime(r.registeredAt)}</TableCell>
                <TableCell className="text-text3">{r.promotedAt ? `${relativeTime(r.promotedAt)}${r.promotedBy ? ` · ${r.promotedBy}` : ""}` : "—"}</TableCell>
                <TableCell className="max-w-xs truncate text-text2">{r.notes || "—"}</TableCell>
                <TableCell className="text-right">
                  <span className="flex justify-end gap-2">
                    {r.channel === "beta" && <Button size="sm" variant="outline" onClick={() => setPromoteTarget(r)} data-testid={`promote-${r.version}`}>Freigeben</Button>}
                    <Button size="sm" onClick={() => setRolloutTarget(r)} data-testid={`rollout-${r.version}`}><Rocket className="h-4 w-4" /> Rollout</Button>
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>
      {registerOpen && <RegisterDialog open={registerOpen} onOpenChange={setRegisterOpen} />}
      {promoteTarget && (
        <ConfirmDialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)} title={`${promoteTarget.version} freigeben`} description="beta → stable: ab jetzt fuer Kunden mit Kanal stable waehlbar. Zurueck gibt es nicht (ein zurueckgezogenes Release bekommt eine neue Nummer)." confirmText="Freigeben" isPending={promote.isPending} errorMessage={promote.isError ? promote.error.message : undefined} onConfirm={() => promote.mutate(promoteTarget.version, { onSuccess: () => setPromoteTarget(null) })} />
      )}
      {rolloutTarget && <RolloutDialog open={!!rolloutTarget} onOpenChange={(o) => !o && setRolloutTarget(null)} release={rolloutTarget} />}
    </div>
  );
}
